"use client";

import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

interface DotLogoProps {
  text?: string;
  /** Logical font size in px (the dot matrix is built around this). */
  size?: number;
  className?: string;
}

/**
 * The wordmark rendered as a dot-matrix that assembles from flying dots — a
 * logo-tuned cousin of ParticleText (coarser dots so it stays legible small,
 * fixed dimensions so it never shifts layout, animates once). Decorative
 * (aria-hidden); the link/heading provides the accessible name.
 *
 * It lives in the persistent header, so it mounts once and the assemble
 * animation plays a single time per full page load (not on every navigation).
 */
export function DotLogo({ text = "Tutorials", size = 28, className }: DotLogoProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const DPR = Math.min(window.devicePixelRatio || 1, 2);
    const css = getComputedStyle(document.documentElement);
    const moss = css.getPropertyValue("--color-primary").trim() || "#1e3a2b";
    const brass = css.getPropertyValue("--color-secondary").trim() || "#8a5616";

    const label = text.toUpperCase();
    const fpx = size * DPR;
    const font = `800 ${fpx}px ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif`;
    const setFont = () => {
      ctx.font = font;
      ctx.textBaseline = "middle";
      ctx.textAlign = "left";
    };

    setFont();
    const padX = 2 * DPR;
    const padY = 5 * DPR;
    const Wd = Math.ceil(ctx.measureText(label).width + padX * 2);
    const Hd = Math.ceil(fpx + padY * 2);

    canvas.width = Wd;
    canvas.height = Hd;
    canvas.style.width = `${Wd / DPR}px`;
    canvas.style.height = `${Hd / DPR}px`;

    setFont(); // resizing the canvas resets the context
    ctx.fillStyle = "#000";
    ctx.fillText(label, padX, Hd / 2);

    const data = ctx.getImageData(0, 0, Wd, Hd).data;
    const gap = Math.max(2, Math.round(fpx / 18)); // fine, refined dot-matrix
    const r = Math.max(0.8, gap * 0.44);

    interface P {
      x: number;
      y: number;
      tx: number;
      ty: number;
      c: string;
    }
    const particles: P[] = [];
    let n = 0;
    for (let y = 0; y < Hd; y += gap) {
      for (let x = 0; x < Wd; x += gap) {
        if (data[(y * Wd + x) * 4 + 3] > 130) {
          const scatter = Hd * 0.9;
          particles.push({
            tx: x,
            ty: y,
            x: reduced ? x : x + (Math.random() - 0.5) * scatter * 2,
            y: reduced ? y : y + (Math.random() - 0.5) * scatter,
            c: n % 10 === 0 ? brass : moss,
          });
          n++;
        }
      }
    }
    ctx.clearRect(0, 0, Wd, Hd);

    let raf = 0;
    const start = performance.now();
    const draw = (now: number) => {
      ctx.clearRect(0, 0, Wd, Hd);
      const t = now - start;
      let moving = false;
      for (const p of particles) {
        let alpha = 1;
        if (!reduced) {
          const k = Math.min(1, t / 650);
          const ease = 1 - Math.pow(1 - k, 3);
          p.x += (p.tx - p.x) * (0.12 + 0.2 * ease);
          p.y += (p.ty - p.y) * (0.12 + 0.2 * ease);
          alpha = 0.3 + 0.7 * ease;
          if (k < 1 || Math.abs(p.tx - p.x) > 0.4 || Math.abs(p.ty - p.y) > 0.4) {
            moving = true;
          }
        }
        ctx.globalAlpha = alpha;
        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.fillStyle = p.c;
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      if (!reduced && moving) raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => cancelAnimationFrame(raf);
  }, [text, size]);

  return <canvas ref={canvasRef} aria-hidden="true" className={cn("block", className)} />;
}
